import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { formatINR, formatDate, formatDateTime } from "@/lib/format";
import { Wallet, Eye, Loader2, Layers, History, Inbox } from "lucide-react";

const STATUS_META = {
  active: { label: "Available offer", cls: "bg-sky-50 text-sky-700" },
  accepted: { label: "Offer accepted", cls: "bg-indigo-50 text-indigo-700" },
  confirmed: { label: "Confirmed — awaiting disbursement", cls: "bg-indigo-50 text-indigo-700" },
  disbursed: { label: "Active loan — repayment due", cls: "bg-emerald-50 text-emerald-700" },
  repaid: { label: "Fully repaid", cls: "bg-slate-100 text-slate-600" },
  cancelled: { label: "Cancelled", cls: "bg-rose-50 text-rose-700" },
  rejected: { label: "Rejected", cls: "bg-rose-50 text-rose-700" },
  expired: { label: "Expired", cls: "bg-slate-100 text-slate-600" },
  superseded: { label: "Replaced by a newer offer", cls: "bg-slate-100 text-slate-500" },
};

const Line = ({ label, value, mono }) => (
  <div className="flex items-center justify-between py-1.5 border-b border-border/50 last:border-0">
    <span className="text-xs text-muted-foreground">{label}</span>
    <span className={`text-sm font-semibold ${mono ? "font-mono" : ""}`}>{value}</span>
  </div>
);

const LoanCard = ({ l, onView, onRepay }) => {
  const meta = STATUS_META[l.status] || { label: l.status, cls: "bg-slate-100 text-slate-600" };
  return (
    <div className="rounded-xl border border-border p-4 bg-white" data-testid={`loan-card-${l.loan_id}`}>
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="text-[11px] text-muted-foreground">Loan ID</p>
          <p className="font-mono text-xs font-semibold" data-testid={`loan-id-${l.loan_id}`}>{l.loan_id.slice(0, 10)}</p>
        </div>
        <span className={`rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${meta.cls}`} data-testid={`loan-status-${l.loan_id}`}>{meta.label}</span>
      </div>
      <div className="mt-3">
        <p className="text-[11px] text-muted-foreground">{l.is_disbursed ? "Loan amount received" : "Offered amount"}</p>
        <p className="font-mono text-xl font-extrabold text-brand">{formatINR(l.is_disbursed ? l.disbursement_amount : l.approved_amount)}</p>
      </div>
      <div className="mt-3">
        <Line label="Repayment amount" value={formatINR(l.repayment_amount)} mono />
        {l.is_disbursed && <Line label="Outstanding" value={formatINR(l.outstanding)} mono />}
        <Line label="Due date" value={l.repayment_date ? formatDate(l.repayment_date) : "—"} />
      </div>
      <div className="mt-4 flex gap-2">
        <Button variant="outline" size="sm" className="flex-1" onClick={() => onView(l.loan_id)} data-testid={`loan-view-${l.loan_id}`}>
          <Eye className="h-4 w-4 mr-1.5" /> View Details
        </Button>
        {l.can_repay && (
          <Button size="sm" className="flex-1 bg-gold hover:bg-gold-dark text-white" onClick={() => onRepay(l.loan_id)} data-testid={`loan-repay-${l.loan_id}`}>
            <Wallet className="h-4 w-4 mr-1.5" /> Repay
          </Button>
        )}
      </div>
      {l.category === "offer" && (
        <p className="mt-2 text-[11px] text-amber-700">This is an offer. No money has been disbursed yet.</p>
      )}
    </div>
  );
};

export const MyLoans = () => {
  const nav = useNavigate();
  const [loans, setLoans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  useEffect(() => {
    api.get("/loans/me").then(({ data }) => setLoans(data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const openDetail = async (loanId) => {
    setDetailLoading(true);
    setDetail({ loading: true });
    try {
      const { data } = await api.get(`/loans/${loanId}`);
      setDetail(data);
    } catch { setDetail(null); } finally { setDetailLoading(false); }
  };
  const repay = (loanId) => nav(`/repay?loan=${loanId}`);

  if (loading) return null;

  const offers = loans.filter((l) => l.category === "offer");
  const current = loans.filter((l) => l.category === "current");
  const history = loans.filter((l) => l.category === "history");

  return (
    <div className="space-y-5" data-testid="my-loans-section">
      {/* Available Loan Offers */}
      <Card className="p-5 sm:p-6 ip-card-shadow">
        <div className="flex items-center gap-2 mb-4">
          <Layers className="h-5 w-5 text-brand" />
          <h2 className="text-lg font-bold tracking-tight">Available Loan Offers</h2>
          <span className="ml-auto text-xs text-muted-foreground" data-testid="offers-count">{offers.length} offer{offers.length === 1 ? "" : "s"}</span>
        </div>
        {offers.length === 0 ? (
          <div className="flex flex-col items-center gap-2 py-8 text-center" data-testid="no-offers-message">
            <Inbox className="h-8 w-8 text-muted-foreground/60" />
            <p className="text-sm text-muted-foreground">No loan offers available yet.</p>
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2">
            {offers.map((l) => <LoanCard key={l.loan_id} l={l} onView={openDetail} onRepay={repay} />)}
          </div>
        )}
      </Card>

      {/* Active Loans (accepted/confirmed/disbursed) */}
      {current.length > 0 && (
        <Card className="p-5 sm:p-6 ip-card-shadow">
          <div className="flex items-center gap-2 mb-4">
            <Wallet className="h-5 w-5 text-emerald-600" />
            <h2 className="text-lg font-bold tracking-tight">Active Loans</h2>
            <span className="ml-auto text-xs text-muted-foreground" data-testid="active-count">{current.length}</span>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            {current.map((l) => <LoanCard key={l.loan_id} l={l} onView={openDetail} onRepay={repay} />)}
          </div>
        </Card>
      )}

      {/* Loan History */}
      {history.length > 0 && (
        <Card className="p-5 sm:p-6 ip-card-shadow">
          <div className="flex items-center gap-2 mb-4">
            <History className="h-5 w-5 text-muted-foreground" />
            <h2 className="text-lg font-bold tracking-tight">Loan History</h2>
            <span className="ml-auto text-xs text-muted-foreground" data-testid="history-count">{history.length}</span>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            {history.map((l) => <LoanCard key={l.loan_id} l={l} onView={openDetail} onRepay={repay} />)}
          </div>
        </Card>
      )}

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent aria-describedby={undefined} data-testid="loan-detail-dialog">
          <DialogHeader><DialogTitle>Loan details</DialogTitle></DialogHeader>
          {detailLoading || detail?.loading ? (
            <div className="grid place-items-center py-10"><Loader2 className="h-6 w-6 animate-spin text-brand" /></div>
          ) : detail ? (
            <div className="space-y-4">
              <div className="rounded-lg bg-slate-50 border border-border p-4">
                <Line label="Loan ID" value={<span className="font-mono text-xs">{detail.loan_id}</span>} />
                <Line label="Application" value={<span className="font-mono text-xs">{detail.reference_number || "—"}</span>} />
                <Line label="Status" value={(STATUS_META[detail.status] || {}).label || detail.status} />
                <Line label="Approved amount" value={formatINR(detail.approved_amount)} mono />
                <Line label="Disbursed amount" value={formatINR(detail.disbursement_amount)} mono />
                <Line label="Interest rate" value={`${detail.interest_rate}%`} />
                <Line label="Tenure" value={`${detail.tenure} ${detail.tenure_unit}`} />
                <Line label="Repayment amount" value={formatINR(detail.repayment_amount)} mono />
                <Line label="Amount paid" value={formatINR(detail.amount_paid)} mono />
                <Line label="Outstanding" value={formatINR(detail.outstanding)} mono />
                <Line label="Due date" value={detail.repayment_date ? formatDate(detail.repayment_date) : "—"} />
              </div>
              <div>
                <p className="text-sm font-semibold mb-1">Repayment history</p>
                {(detail.repayments || []).length === 0 ? (
                  <p className="text-xs text-muted-foreground">No confirmed repayments yet for this loan.</p>
                ) : (
                  <div className="space-y-1.5">
                    {detail.repayments.map((r) => (
                      <div key={r.id} className="flex items-center justify-between text-sm border-b border-border/50 py-1.5">
                        <span className="font-mono">{formatINR(r.amount)}</span>
                        <span className="text-xs text-muted-foreground">{formatDateTime(r.confirmed_at || r.at)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
};
